import React, { FC } from 'react'
import { Flex, InputNumber, Segmented, Select, Typography } from 'antd'
import { ClockCircleOutlined } from '@ant-design/icons'
import { daysToSeconds, hoursToSeconds } from '@pushly/aqe/lib/utils'
import { CUSTOM_PRESET, detectPreset as detectPresetMatch } from './sticky-preset'
import './vespa-content-window.scss'

const { Text } = Typography

export type ContentWindow = {
    value: number | null
    unit: 'hours' | 'days' | 'weeks'
}

/**
 * Convert a value+unit pair to seconds. AQE's time utils don't have a 'weeks'
 * metric, so weeks are derived as 7 days via daysToSeconds.
 */
function unitToSeconds(value: number, unit: ContentWindow['unit']): number {
    switch (unit) {
        case 'hours':
            return hoursToSeconds(value)
        case 'days':
            return daysToSeconds(value)
        case 'weeks':
            return daysToSeconds(value * 7)
    }
}

export type ContentWindowSelection = string // preset key, or CUSTOM_CONTENT_WINDOW

type VespaContentWindowProps = {
    window: ContentWindow
    /**
     * Explicit selection state, owned by the caller — mirrors StrategySelection
     * for VespaStrategyPresets. Kept separate from `window` so "Custom" can be
     * selected (and stick) even when the current value/unit happens to match a
     * named preset, the same way clicking Custom on ranking weights doesn't
     * require the weights to actually differ from the active preset.
     */
    activeSelection: ContentWindowSelection
    onChange: (window: ContentWindow, selection: ContentWindowSelection) => void
    readonly?: boolean
}

const UNIT_OPTIONS = [
    { value: 'hours', label: 'hours' },
    { value: 'days', label: 'days' },
    { value: 'weeks', label: 'weeks' },
]

type ContentWindowPresetOption = {
    key: string
    label: string
    value: number
    unit: ContentWindow['unit']
}

export const CONTENT_WINDOW_PRESETS: ContentWindowPresetOption[] = [
    { key: '6h', label: 'Last 6 hours', value: 6, unit: 'hours' },
    { key: '12h', label: 'Last 12 hours', value: 12, unit: 'hours' },
    { key: '24h', label: 'Last 24 hours', value: 24, unit: 'hours' },
    { key: '7d', label: 'Last 7 days', value: 7, unit: 'days' },
    { key: '30d', label: 'Last 30 days', value: 30, unit: 'days' },
]

/**
 * A reflected, non-applicable selection — mirrors CUSTOM_STRATEGY in
 * vespa-strategy-presets.tsx (both are the shared CUSTOM_PRESET sentinel
 * from sticky-preset.ts, re-exported under this domain's own name). It has
 * no value/unit of its own. Unlike a named preset key, clicking it explicitly
 * is a no-op that retains the current value/unit but switches the
 * *selection* into hand-tuning mode — the caller must track this as separate
 * state (ContentWindowSelection) rather than re-deriving it from value/unit
 * on every render, otherwise clicking Custom while already on a matching
 * preset would have nothing to change and no visible effect (the same bug
 * CUSTOM_STRATEGY was introduced to avoid for ranking weights).
 */
export const CUSTOM_CONTENT_WINDOW = CUSTOM_PRESET

/**
 * Determines which named preset (if any) matches the given window — used to
 * auto-snap the selection when a value is hand-typed (mirrors detectPreset
 * for ranking weights: typing/dragging to an exact preset value re-selects
 * that preset, it isn't forced into Custom just because it came from a
 * free-form control). Normalizes both sides to seconds so e.g. "1 weeks"
 * matches "Last 7 days". Returns CUSTOM_CONTENT_WINDOW when nothing matches.
 * Delegates the actual matching to the shared sticky-preset module — both
 * sides are compared as seconds (null when the window has no value) so the
 * shared TValue is a single consistent type.
 */
export function detectContentWindowPreset(win: ContentWindow): string {
    const presetSecondsByKey = Object.fromEntries(
        CONTENT_WINDOW_PRESETS.map((p) => [p.key, unitToSeconds(p.value, p.unit)]),
    ) as Record<string, number>
    const winSeconds = win.value ? unitToSeconds(win.value, win.unit) : null

    return detectPresetMatch(presetSecondsByKey, winSeconds, (presetSeconds, current) => presetSeconds === current)
}

const PRESET_SEGMENTS = [
    ...CONTENT_WINDOW_PRESETS.map((p) => ({ value: p.key, label: p.label })),
    { value: CUSTOM_CONTENT_WINDOW, label: 'Custom' },
]

/**
 * Content window selector — controls freshness_max_age.
 * "Only content published in this window is eligible."
 *
 * Every config must have an active window (no UI path leaves this unset —
 * see secondsToContentWindow's default and vespa-configuration-editor.tsx's
 * mount-time self-heal for legacy configs saved before this control existed).
 */
export const VespaContentWindow: FC<VespaContentWindowProps> = ({
    window: win,
    activeSelection,
    onChange,
    readonly,
}) => {
    return (
        <Flex vertical gap="var(--space-2)">
            <Flex gap="var(--space-2)" align="center" wrap="wrap">
                <ClockCircleOutlined style={{ color: 'var(--color-gray-dark)' }} aria-hidden="true" />
                <Text>Published in the last</Text>
                <InputNumber
                    min={1}
                    max={999}
                    disabled={readonly}
                    value={win.value}
                    onChange={(v) => {
                        const next = { ...win, value: v }
                        onChange(next, detectContentWindowPreset(next))
                    }}
                    style={{ width: 80 }}
                />
                <Select
                    disabled={readonly}
                    value={win.unit}
                    onChange={(unit) => {
                        const next = { ...win, unit }
                        onChange(next, detectContentWindowPreset(next))
                    }}
                    options={UNIT_OPTIONS}
                    style={{ width: 100 }}
                />
            </Flex>
            <Segmented<string>
                classNames={{
                    root: 'vespa-content-window-presets',
                    item: 'vespa-content-window-presets__item',
                    label: 'vespa-content-window-presets__label',
                    icon: 'vespa-content-window-presets__icon',
                }}
                size="small"
                options={PRESET_SEGMENTS}
                value={activeSelection}
                block={false}
                onChange={(key) => {
                    if (key === CUSTOM_CONTENT_WINDOW) {
                        // Custom retains the current value/unit and switches
                        // the selection into hand-tuning mode — mirrors
                        // handleStrategySelect's CUSTOM_STRATEGY branch.
                        // Nothing to persist since the window is unchanged.
                        onChange(win, CUSTOM_CONTENT_WINDOW)
                        return
                    }
                    const match = CONTENT_WINDOW_PRESETS.find((p) => p.key === key)!
                    onChange({ value: match.value, unit: match.unit }, key)
                }}
            />
        </Flex>
    )
}

/** Convert ContentWindow to seconds for freshness_max_age. */
export function contentWindowToSeconds(win: ContentWindow): number | null {
    if (!win.value || win.value <= 0) return null
    return unitToSeconds(win.value, win.unit)
}

/**
 * Convert freshness_max_age seconds to ContentWindow.
 *
 * Every config edited through this UI is required to have an active window
 * (see VespaContentWindow's docs and the mount-time self-heal in
 * vespa-configuration-editor.tsx) — there's deliberately no UI path that
 * leaves it unset. A null/zero/negative input here means a legacy config
 * saved before this requirement existed; it falls back to a concrete
 * default (12 hours) exactly as any other missing field would, rather than
 * modeling "unset" as a distinct UI state.
 */
export function secondsToContentWindow(seconds: number | null | undefined): ContentWindow {
    if (!seconds || seconds <= 0) return { value: 12, unit: 'hours' }

    // Prefer a named preset's own value/unit so the number/unit inputs agree
    // with the highlighted preset pill: 86400s reads back as "24 hours" (not
    // "1 day") and 604800s as "7 days" (not "1 week"), rather than the
    // largest-whole-unit form below promoting them to a coarser unit than the
    // preset expresses.
    const preset = CONTENT_WINDOW_PRESETS.find((p) => unitToSeconds(p.value, p.unit) === seconds)
    if (preset) return { value: preset.value, unit: preset.unit }

    if (seconds % unitToSeconds(1, 'weeks') === 0) return { value: seconds / unitToSeconds(1, 'weeks'), unit: 'weeks' }
    if (seconds % unitToSeconds(1, 'days') === 0) return { value: seconds / unitToSeconds(1, 'days'), unit: 'days' }
    return { value: Math.round(seconds / unitToSeconds(1, 'hours')), unit: 'hours' }
}
